<template>
    <div :class="`address-suggestions ${suggestedAddresses.length ? '' : 'd-none'}`">
        <span aria-live="polite" role="dialog">{{$t('label.address.suggestions', [suggestedAddresses.length])}}</span>
        <ul>
            <li v-for="(address, index) in suggestedAddresses" :key="index + 'suggestedAddress'" tabindex="0"
                @click="selectAddress($event, address)"
                @keydown.enter.space="selectAddress($event, address)"
                @keydown.escape="setSuggestedAddresses([]); $event.target.closest('.address-suggestions').previousElementSibling.querySelector('input').focus();"
                @keydown.down.up.tab.esc="handleAddressSuggestionFocus($event)"
                >
                <span>
                    <strong>{{ address.Text }}</strong> <span>{{ address.Description }}</span>
                </span>
                <span class="icon-chevron" v-if="address.Type !== 'Address'">
                    <SvgHelper :icon="'chevron'" :svgtitle="'chevron'" :decorative="true"></SvgHelper>
                </span>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { onMounted, computed, ref, nextTick } from "vue";
import { EventBus as oldEventBus } from "@/scripts/event-bus.js";
import { SvgHelper } from "@/components/helper/util"
import { mapActions, mapGetters, mapMutations } from "@/store/helper/vuex";
import { handleAddressSuggestionFocus, handleAddressInputArrow } from "@/components/helper/checkout/checkoutHelper"

const { getSuggestedAddresses, retrieveAddress, updateFormField } = mapActions();
const { suggestedAddresses, currentStage, formFields, shippingDynamicStructure, billingDynamicStructure, billingLocale, shippingLocale } = mapGetters();
const { setSuggestedAddresses } = mapMutations();

const currentDynamicStructure = computed(() => { return currentStage && currentStage.value === 'shipping' ? shippingDynamicStructure.value[shippingLocale.value] : billingDynamicStructure.value[billingLocale.value] });

const currentFormFieldName = computed(() => {
    return currentStage && currentStage.value === 'shipping' ? 'shipping' : 'billing';
});

const addressMapping = {
    address1: 'Line1',
    address2: 'Line2',
    city: 'City',
    postalCode: 'PostalCode',
    stateCode: 'ProvinceCode'
}

const currentText = ref('');

document.addEventListener('documentClicked', (data) => {
    if (suggestedAddresses.value.length &&
        !data.detail.target.closest('.address-suggestions') &&
        !data.detail.target.closest('#shippingAddressOne') &&
        !data.detail.target.closest('#billingAddressOne')) {
        setSuggestedAddresses([]);
    }
});

const selectAddress = (event, address) => {
    if (address.Type === 'Address') {
        retrieveAddress({
            id: address.Id
        })
            .then((responseData) => {
                if (responseData.data.Items.length === 0 || (responseData.data.Items.length == 1 && typeof responseData.data.Items[0].Error != 'undefined')) {
                    return;
                }

                const address = responseData.data.Items[0];

                for (const [sfccKey, locateKey] of Object.entries(addressMapping)) {
                    if (address['CountryIso2'] === 'HK' && (sfccKey === 'city' || sfccKey === 'stateCode')) {
                        continue;
                    }
                    if (sfccKey in formFields.value[currentFormFieldName.value] && address[locateKey]) {
                        updateFormField({
                            formName: currentFormFieldName.value,
                            fieldName: sfccKey,
                            fieldValue: address[locateKey]
                        });
                        nextTick(() => {
                            oldEventBus.$emit('fieldValidation', {
                                FormName: currentFormFieldName.value,
                                field: currentDynamicStructure.value.get(sfccKey),
                            });
                        });
                    }
                }

                if (currentFormFieldName.value === 'shipping') {
                    setSuggestedAddresses([]);
                    nextTick(() => {
                        oldEventBus.$emit('updateShippingMethodDynamicFields');
                    });
                } else {
                    setSuggestedAddresses([]);
                }
            })
            .catch(() => {
            });
    } else {
        getSuggestedAddresses({
            container: address.Id,
            text: currentText.value,
            formName: currentFormFieldName.value
        }).then(() => {
            var firstAddressSuggestion = event.target.closest('.address-suggestions').querySelector('[tabindex="0"]');
            if (firstAddressSuggestion) {
                firstAddressSuggestion.focus();
            }
        });
    }
}

oldEventBus.$off('getAddressSuggestions');
oldEventBus.$on('getAddressSuggestions', (data) => {
    currentText.value = data;
    getSuggestedAddresses({
        container: '',
        text: data,
        formName: currentFormFieldName.value
    });
});

onMounted(() => {
    handleAddressInputArrow();
});

</script>